/* The 3D chart: whether it is on, and how hard it is exaggerated.
 *
 * A MODE, like anchor/underway, and per device (Tier 2) for the same reason the
 * gauge column is: which screen you are standing at decides whether you want a
 * perspective view. The cockpit iPad at anchor and a phone below decks want
 * different things.
 *
 * ═══ THIS IS A VIEW, NOT A CHART ═══
 *
 * Everything in perspective lies about distance — two marks the same number of
 * pixels apart are not the same distance away, and the further one is
 * compressed toward the horizon. The 2D north-up chart remains the working
 * chart and is not modified by any of this; 3D exists to show you the SHAPE of
 * the water you are in, which is a thing no number on the depth gauge conveys.
 *
 * The seabed is drawn 2.5× deeper than it is (see SEABED_GAIN) on top of
 * whatever this exaggeration says, and Seascape's BC vertical datum is
 * self-declared "unverified" and shallow-biased. So the multiplier is shown as
 * a number in the panel rather than hidden behind a pretty slider.
 */

const KEY_ON = 'gd.3d';
const KEY_EX = 'gd.3d.ex';

export const MIN_EXAGGERATION = 1;
export const MAX_EXAGGERATION = 3;
/* 1.4 on top of the baked 2.5× seabed gain. Enough that a channel reads as a
 * channel from the cockpit; not so much that the Gulf Islands look like the
 * Marianas. */
export const DEFAULT_EXAGGERATION = 1.4;

const clamp = (v: number) => Math.max(MIN_EXAGGERATION, Math.min(MAX_EXAGGERATION, v));

function loadEx(): number {
  const raw = Number(localStorage.getItem(KEY_EX));
  return Number.isFinite(raw) && raw > 0 ? clamp(raw) : DEFAULT_EXAGGERATION;
}

export const threeD = $state<{ on: boolean; exaggeration: number; seamarks: boolean }>({
  on: localStorage.getItem(KEY_ON) === '1',
  exaggeration: loadEx(),
  seamarks: localStorage.getItem('gd.3d.marks') !== '0'
});

const save = (k: string, v: string) => {
  try {
    localStorage.setItem(k, v);
  } catch {
    /* the view still works, it just forgets by morning */
  }
};

export function set3D(on: boolean): void {
  threeD.on = on;
  save(KEY_ON, on ? '1' : '0');
}

export const toggle3D = (): void => set3D(!threeD.on);

export function setExaggeration(v: number): void {
  threeD.exaggeration = clamp(v);
  save(KEY_EX, String(threeD.exaggeration));
}

export function setSeamarks3D(on: boolean): void {
  threeD.seamarks = on;
  save('gd.3d.marks', on ? '1' : '0');
}

/* ---- the sky ----
 *
 * Fog needs 3D terrain, which is why this lives with the mode rather than in
 * the style. Aerial perspective is most of what makes distance readable in a
 * pitched view: without it the horizon is a hard line and everything past a
 * mile reads as equally far away.
 *
 * ═══ fog-ground-blend IS A DISTANCE, AND IT READS BACKWARDS ═══
 *
 * The spec: "How to blend the fog over the 3D terrain. WHERE 0 IS THE MAP
 * CENTER AND 1 IS THE HORIZON." So it is where the fog STARTS, and small
 * numbers mean fog begins at your own boat. The first pass used 0.16, which
 * put haze over the whole view and made everything past a mile read as
 * weather. Held out at 0.85 the fog does what it should: nothing at all across
 * the anchorage, softening only in the last stretch before the horizon, so
 * distance is legible instead of milky.
 *
 * horizon-fog-blend is pulled down for the same reason — 0 uses the horizon
 * colour alone and 1 the fog colour alone, and a fog-heavy horizon is a white
 * band where the land should be.
 */
export const skyFor = (night: boolean) =>
  night
    ? {
        'sky-color': '#040a16',
        'horizon-color': '#0c1a2e',
        'fog-color': '#060d18',
        'fog-ground-blend': 0.9,
        'horizon-fog-blend': 0.3,
        'sky-horizon-blend': 0.7,
        'atmosphere-blend': 0.8
      }
    : {
        'sky-color': '#4a86c4',
        'horizon-color': '#cfe4f2',
        'fog-color': '#cfe0ec',
        'fog-ground-blend': 0.85,
        'horizon-fog-blend': 0.35,
        'sky-horizon-blend': 0.8,
        'atmosphere-blend': 0.9
      };
